import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export type AmenityCategory = 'general' | 'food_drink' | 'wellness' | 'business' | 'room' | 'outdoor';
export type LanguageCode = 'tr' | 'en' | 'de' | 'ru';

export interface IAmenityTranslation {
  amenityId: number;
  languageCode: LanguageCode;
  name: string;
  description?: string;
}

export interface IAmenity {
  id: number;
  amenityKey: string;
  category: AmenityCategory;
  iconName?: string;
  sortOrder?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  translations?: IAmenityTranslation[];
}

export interface IAmenitiesState {
  amenities: IAmenity[];
  amenitiesByCategory: Record<AmenityCategory, IAmenity[]>;
  currentAmenity?: IAmenity;
  loading: boolean;
  error?: string;
  totalResults?: number;
  totalPages?: number;
  page?: number;
  limit?: number;
}

const initialState: IAmenitiesState = {
  amenities: [],
  amenitiesByCategory: {
    general: [],
    food_drink: [],
    wellness: [],
    business: [],
    room: [],
    outdoor: [],
  },
  currentAmenity: undefined,
  loading: false,
  error: undefined,
};

interface IQueryParams {
  category?: AmenityCategory;
  isActive?: boolean;
  sortBy?: string;
  limit?: number;
  page?: number;
}

interface IQueryResponse {
  results: IAmenity[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

// Async Thunks
export const fetchAmenities = createAsyncThunk(
  'amenities/fetchAll',
  async (params?: IQueryParams) => {
    const res = await axios.get<IQueryResponse>(`${REACT_APP_API_URL}/amenities`, { params });
    return res.data;
  }
);

export const fetchAllAmenitiesByCategory = createAsyncThunk(
  'amenities/fetchAllByCategory',
  async (languageCode: LanguageCode = 'tr') => {
    const res = await axios.get<Record<AmenityCategory, IAmenity[]>>(`${REACT_APP_API_URL}/amenities/by-category`, {
      params: { lang: languageCode }
    });
    return res.data;
  }
);

export const fetchAmenityById = createAsyncThunk(
  'amenities/fetchById',
  async (amenityId: number) => {
    const res = await axios.get<IAmenity>(`${REACT_APP_API_URL}/amenities/${amenityId}`);
    return res.data;
  }
);

export const createAmenity = createAsyncThunk(
  'amenities/create',
  async (data: {
    amenityKey: string;
    category: AmenityCategory;
    iconName?: string;
    sortOrder?: number;
    isActive?: boolean;
    translations: Array<{ languageCode: LanguageCode; name: string; description?: string }>;
  }) => {
    const res = await axios.post<IAmenity>(`${REACT_APP_API_URL}/amenities`, data);
    return res.data;
  }
);

export const updateAmenity = createAsyncThunk(
  'amenities/update',
  async ({ amenityId, data }: {
    amenityId: number;
    data: {
      amenityKey?: string;
      category?: AmenityCategory;
      iconName?: string;
      sortOrder?: number;
      isActive?: boolean;
      translations?: Array<{ languageCode: LanguageCode; name: string; description?: string }>;
    };
  }) => {
    const res = await axios.put<IAmenity>(`${REACT_APP_API_URL}/amenities/${amenityId}`, data);
    return res.data;
  }
);

export const deleteAmenity = createAsyncThunk(
  'amenities/delete',
  async (amenityId: number) => {
    await axios.delete(`${REACT_APP_API_URL}/amenities/${amenityId}`);
    return amenityId;
  }
);

const amenitiesSlice = createSlice({
  name: 'amenities',
  initialState,
  reducers: {
    clearAmenities: (state) => {
      state.amenities = [];
      state.amenitiesByCategory = {
        general: [],
        food_drink: [],
        wellness: [],
        business: [],
        room: [],
        outdoor: [],
      };
      state.currentAmenity = undefined;
      state.loading = false;
      state.error = undefined;
    },
    clearCurrentAmenity: (state) => {
      state.currentAmenity = undefined;
    },
    setCurrentAmenity: (state, action: PayloadAction<IAmenity>) => {
      state.currentAmenity = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Amenities
    builder.addCase(fetchAmenities.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchAmenities.fulfilled, (state, action) => {
      state.loading = false;
      state.amenities = action.payload.results;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder.addCase(fetchAmenities.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Fetch All Amenities By Category
    builder.addCase(fetchAllAmenitiesByCategory.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchAllAmenitiesByCategory.fulfilled, (state, action) => {
      state.loading = false;
      state.amenitiesByCategory = action.payload;
    });
    builder.addCase(fetchAllAmenitiesByCategory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Fetch Amenity By Id
    builder.addCase(fetchAmenityById.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchAmenityById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentAmenity = action.payload;
    });
    builder.addCase(fetchAmenityById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Create Amenity
    builder.addCase(createAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(createAmenity.fulfilled, (state, action) => {
      state.loading = false;
      state.amenities.push(action.payload);
      state.currentAmenity = action.payload;
      // Also add to category
      if (action.payload.category) {
        state.amenitiesByCategory[action.payload.category].push(action.payload);
      }
    });
    builder.addCase(createAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Update Amenity
    builder.addCase(updateAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(updateAmenity.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.amenities.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.amenities[index] = action.payload;
      }
      state.currentAmenity = action.payload;
      // Update in category
      if (action.payload.category) {
        const categoryIndex = state.amenitiesByCategory[action.payload.category].findIndex(
          (a) => a.id === action.payload.id
        );
        if (categoryIndex !== -1) {
          state.amenitiesByCategory[action.payload.category][categoryIndex] = action.payload;
        }
      }
    });
    builder.addCase(updateAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Delete Amenity
    builder.addCase(deleteAmenity.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteAmenity.fulfilled, (state, action) => {
      state.loading = false;
      state.amenities = state.amenities.filter((a) => a.id !== action.payload);
      // Remove from categories
      Object.keys(state.amenitiesByCategory).forEach((category) => {
        state.amenitiesByCategory[category as AmenityCategory] = state.amenitiesByCategory[
          category as AmenityCategory
        ].filter((a) => a.id !== action.payload);
      });
      if (state.currentAmenity?.id === action.payload) {
        state.currentAmenity = undefined;
      }
    });
    builder.addCase(deleteAmenity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const { clearAmenities, clearCurrentAmenity, setCurrentAmenity } = amenitiesSlice.actions;

export default amenitiesSlice.reducer;
